import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export default function CTASection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { t, i18n } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const getStyle = (delay: number = 0): React.CSSProperties => ({
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0) scale(1)" : "translateY(28px) scale(0.97)",
    transition: "opacity 0.7s ease-out, transform 0.7s ease-out",
    transitionDelay: `${delay}ms`,
  });

  return (
    <section id="download" ref={sectionRef} className="relative py-0 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img
          src="/cta-bg.jpg"
          alt="ダウンロードCTA背景"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-black/75" />
        {/* Visipo Blue tint */}
        <div className="absolute inset-0 opacity-20" style={{ background: "radial-gradient(ellipse at center, #13B6EC, transparent 70%)" }} />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-8 py-24 md:py-36 text-center">
        {/* Floating top label */}
        <div
          className="cta-reveal inline-flex items-center gap-2 px-5 py-2 rounded-full text-xs font-bold tracking-widest uppercase mb-8 glass-card-subtle text-white/80"
          style={getStyle(0)}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#13B6EC] animate-pulse-gentle" />
          {t('cta.label')}
        </div>

        {/* Headline */}
        <h2
          className="cta-reveal text-4xl md:text-5xl lg:text-6xl font-black leading-tight mb-6"
          style={{ ...getStyle(120), fontFamily: "'Noto Sans JP', sans-serif" }}
        >
          <span className="text-white block">{t('cta.title1')}</span>
          <span
            className="block mt-1"
            style={{
              fontFamily: "'Noto Serif JP', serif",
              color: "#5FCEEF",
              fontStyle: "italic",
            }}
          >
            {t('cta.title2')}
          </span>
        </h2>

        <p
          className="cta-reveal text-base md:text-lg text-white/70 max-w-xl mx-auto mb-10 leading-relaxed"
          style={{ ...getStyle(240), fontFamily: "'Noto Sans JP', sans-serif" }}
        >
          {t('cta.desc').split('\n').map((line, i) => <span key={i}>{line}<br /></span>)}
        </p>

        {/* Download Buttons */}
        <div className="cta-reveal flex flex-col sm:flex-row items-center justify-center gap-4 mb-8" style={getStyle(360)}>
          <a
            href={i18n.language.startsWith('en') ? "https://apps.apple.com/us/app/visipo/id6761425755" : "https://apps.apple.com/jp/app/visipo/id6761425755"}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-7 py-4 rounded-2xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer group"
            style={{ background: "rgba(255,255,255,0.95)", color: "#111" }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.transform = "translateY(-3px)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.transform = ""; }}
          >
            <i className="ri-apple-fill text-2xl text-gray-800" />
            <div className="text-left">
              <div className="text-[10px] text-gray-500 font-normal leading-none">{t('cta.appStore_sub')}</div>
              <div className="text-base font-bold text-gray-900">{t('cta.appStore')}</div>
            </div>
          </a>
          <div
            className="flex items-center gap-3 px-7 py-4 rounded-2xl font-semibold whitespace-nowrap opacity-50 cursor-not-allowed"
            style={{ background: "rgba(255,255,255,0.6)", color: "#999" }}
          >
            <i className="ri-google-play-fill text-2xl text-gray-400" />
            <div className="text-left">
              <div className="text-[10px] text-gray-400 font-normal leading-none">{t('cta.playStore_sub')}</div>
              <div className="text-base font-bold text-gray-500">{t('cta.playStore')}</div>
            </div>
          </div>
        </div>

        {/* System requirements */}
        <p className="cta-reveal text-xs text-white/40" style={{ ...getStyle(480), fontFamily: "'Noto Sans JP', sans-serif" }}>
          {t('cta.requirements')}
        </p>

      </div>
    </section>
  );
}
